/* iter55z-j — SelectionActionPopover: floating "highlight actions" UI.
 *
 * When the user selects text inside the manuscript, this popover
 * appears near the selection and offers three actions:
 *   1. Suggest edit  → opens the create-suggestion form with a
 *                      "proposed_text" field prefilled from the anchor
 *   2. Add note      → creates a comment-kind suggestion
 *   3. Bookmark      → forwards to the parent's bookmark handler
 *
 * The popover self-hides on outside click / scroll / selection collapse.
 * Uses fixed-position rects (relative to the viewport) so it works
 * inside scroll containers without breaking placement.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bookmark, MessageSquare, PenSquare, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const POP_W = 340;
const POP_H_COMPACT = 44;

export default function SelectionActionPopover({
  scrollAreaRef,          // ref to the manuscript scroll container
  projectId,
  chapterId,
  onBookmark,             // (selection_text) => void
  onSuggestionCreated,    // () => void — parent refresh
}) {
  const [pos, setPos]           = useState(null);      // {x, y}
  const [sel, setSel]           = useState('');
  const [mode, setMode]         = useState('menu');    // menu | edit | note
  const [proposed, setProposed] = useState('');
  const [note, setNote]         = useState('');
  const [busy, setBusy]         = useState(false);
  const popRef = useRef(null);

  // Watch selection changes anywhere inside the scroll container.
  const updateFromSelection = useCallback(() => {
    const s = window.getSelection();
    if (!s || s.isCollapsed) { setPos(null); setSel(''); setMode('menu'); return; }
    const text = s.toString().trim();
    if (!text || text.length < 3) { setPos(null); setSel(''); setMode('menu'); return; }
    const container = scrollAreaRef?.current;
    if (container && s.anchorNode && !container.contains(s.anchorNode)) {
      // Selection isn't inside our editor — ignore.
      setPos(null); return;
    }
    const range = s.getRangeAt(0);
    const rect  = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) { setPos(null); return; }
    // Position above the selection, clamped to viewport.
    const x = Math.min(window.innerWidth - POP_W - 12, Math.max(12, rect.left + rect.width / 2 - POP_W / 2));
    const y = Math.max(12, rect.top - POP_H_COMPACT - 8);
    setPos({ x, y });
    setSel(text);
    if (mode === 'menu') { setProposed(''); setNote(''); }
  }, [scrollAreaRef, mode]);

  useEffect(() => {
    const onUp = () => setTimeout(updateFromSelection, 10);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('keyup', onUp);
    return () => {
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('keyup', onUp);
    };
  }, [updateFromSelection]);

  // Prevent the browser from collapsing an existing free-form selection
  // when the user right-clicks to trigger this popover. Without this,
  // right-clicking after dragging to select multiple words resets the
  // selection to the single word under the cursor before mouseup fires.
  useEffect(() => {
    const onMouseDown = (e) => {
      if (e.button !== 2) return; // right-click only
      const s = window.getSelection();
      if (!s || s.isCollapsed) return; // nothing selected — let browser handle it
      const text = s.toString().trim();
      if (text.length < 3) return; // too short to be meaningful
      const container = scrollAreaRef?.current;
      if (container && e.target && !container.contains(e.target)) return; // outside editor
      // Preserve the existing selection by preventing the browser from
      // resetting it during right-click mousedown.
      e.preventDefault();
    };
    document.addEventListener('mousedown', onMouseDown, true); // capture phase
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [scrollAreaRef]);

  // Close on outside click / scroll of the manuscript area.
  useEffect(() => {
    if (!pos) return undefined;
    const container = scrollAreaRef?.current;
    const onDocClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) {
        setTimeout(() => {
          const s = window.getSelection();
          if (!s || s.isCollapsed) setPos(null);
        }, 20);
      }
    };
    const onScroll = () => updateFromSelection();
    document.addEventListener('mousedown', onDocClick);
    container?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      container?.removeEventListener('scroll', onScroll);
    };
  }, [pos, scrollAreaRef, updateFromSelection]);

  const create = async (kind) => {
    if (!projectId || !chapterId || !sel) return;
    setBusy(true);
    try {
      const body = {
        chapter_id:    chapterId,
        kind,
        anchor_text:   sel,
        proposed_text: kind === 'edit' ? proposed.trim() : null,
        note:          note.trim() || null,
      };
      await axios.post(`${API}/projects/${projectId}/suggestions`, body, { withCredentials: true });
      toast.success(kind === 'edit' ? 'Suggestion sent.' : 'Note added.');
      setPos(null); setMode('menu'); setProposed(''); setNote('');
      window.getSelection()?.removeAllRanges();
      onSuggestionCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save.');
    } finally { setBusy(false); }
  };

  if (!pos) return null;

  return (
    <div
      ref={popRef}
      data-testid="editor-selection-popover"
      className="fixed z-50 rounded-md border border-panel-border bg-card shadow-lg"
      style={{ left: pos.x, top: pos.y, width: POP_W }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {mode === 'menu' && (
        <div className="flex items-center justify-between gap-1 p-1.5">
          <Button
            variant="ghost" size="sm"
            onClick={() => setMode('edit')}
            className="h-8 gap-1.5 px-2 text-[13.5px]"
            data-testid="editor-selection-suggest-edit"
          >
            <PenSquare className="h-3.5 w-3.5 text-primary" /> Suggest edit
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => setMode('note')}
            className="h-8 gap-1.5 px-2 text-[13.5px]"
            data-testid="editor-selection-add-note"
          >
            <MessageSquare className="h-3.5 w-3.5 text-emerald-600" /> Add note
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => { onBookmark?.(sel); setPos(null); }}
            className="h-8 gap-1.5 px-2 text-[13.5px]"
            data-testid="editor-selection-bookmark"
          >
            <Bookmark className="h-3.5 w-3.5 text-amber-500" /> Bookmark
          </Button>
          <Button
            variant="ghost" size="sm"
            onClick={() => setPos(null)}
            className="h-8 w-8 p-0"
            aria-label="Close"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
      {mode === 'edit' && (
        <div className="p-2.5 text-[13.5px]" data-testid="editor-selection-edit-form">
          <div className="mb-1 truncate text-[13px] uppercase tracking-wider text-muted-foreground">
            Suggest edit
          </div>
          <div className="mb-1.5 rounded-sm bg-secondary/50 p-1.5 text-[13.5px] italic text-foreground/80">
            &ldquo;{sel.length > 120 ? sel.slice(0, 120) + '…' : sel}&rdquo;
          </div>
          <Textarea
            value={proposed}
            onChange={(e) => setProposed(e.target.value)}
            placeholder="Your proposed replacement…"
            className="min-h-[60px] text-[14px]"
            data-testid="editor-selection-proposed-input"
          />
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional note to the author"
            className="mt-2 min-h-[46px] text-[13.5px]"
            data-testid="editor-selection-note-input-edit"
          />
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 text-[13px]" onClick={() => setMode('menu')}>
              Back
            </Button>
            <Button
              size="sm"
              disabled={busy || !proposed.trim()}
              onClick={() => create('edit')}
              className="h-7 gap-1.5 text-[13px]"
              data-testid="editor-selection-suggest-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send
            </Button>
          </div>
        </div>
      )}
      {mode === 'note' && (
        <div className="p-2.5 text-[13.5px]" data-testid="editor-selection-note-form">
          <div className="mb-1 text-[13px] uppercase tracking-wider text-muted-foreground">
            Add note
          </div>
          <div className="mb-1.5 rounded-sm bg-secondary/50 p-1.5 text-[13.5px] italic text-foreground/80">
            &ldquo;{sel.length > 120 ? sel.slice(0, 120) + '…' : sel}&rdquo;
          </div>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Comment or question for the author"
            className="min-h-[60px] text-[14px]"
            data-testid="editor-selection-note-input"
          />
          <div className="mt-2 flex items-center justify-end gap-1.5">
            <Button variant="ghost" size="sm" className="h-7 text-[13px]" onClick={() => setMode('menu')}>
              Back
            </Button>
            <Button
              size="sm"
              disabled={busy || !note.trim()}
              onClick={() => create('comment')}
              className="h-7 gap-1.5 text-[13px]"
              data-testid="editor-selection-note-submit"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Save note
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
